import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "./api";
import {
  formatProfileNumberValue,
  getProfileNumberDisplay,
  isProfileNoDisplaySearchMode,
  PROFILE_NO_DISPLAY_SEARCH_KEY,
} from "./profileNumber";
import { hasPermission } from "./rbac";
import AssignedProfilePerformanceModal from "./AssignedProfilePerformanceModal";

const TASK_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

export default function TaskAssignment({
  token,
  user,
  onSwitchToAssignedToMe,
  onOpenBiodataWorkflow,
  onViewProfile,
  forcedTaskTab = null,
  forcedWorkspaceTab = null,
}) {
  const canAssignProfiles = hasPermission(user, "profiles", "assign");
  const canUnassignProfiles = hasPermission(user, "profiles", "unassign");
  const canViewTasks = hasPermission(user, "tasks", "view");
  const canCreateTasks = hasPermission(user, "tasks", "create");
  const canUpdateTasks = hasPermission(user, "tasks", "update");
  const canCompleteTasks = hasPermission(user, "tasks", "complete");
  const canReassignTasks = hasPermission(user, "tasks", "reassign");
  const canViewBiodata = hasPermission(user, "biodata_assignments", "view");
  const canAssignBiodata = hasPermission(user, "biodata_assignments", "assign");
  const canUpdateBiodata = hasPermission(user, "biodata_assignments", "update");
  const canCompleteBiodata = hasPermission(user, "biodata_assignments", "complete");
  const canManageTaskUsers = canCreateTasks || canReassignTasks;
  const hasTaskWorkspaceAccess =
    canViewTasks || canCreateTasks || canUpdateTasks || canCompleteTasks || canReassignTasks;
  const canViewBiodataAssignments = canViewBiodata || canAssignBiodata || canUpdateBiodata || canCompleteBiodata;
  const isSuperAdmin = Boolean(user?.isSuperAdmin);
  const currentUserEmail = String(user?.email || "").trim().toLowerCase();
  const defaultWorkspaceTab = hasTaskWorkspaceAccess ? "tasks" : canAssignProfiles ? "profiles" : "biodata";
  const defaultTaskTab = canCreateTasks || canReassignTasks || canUpdateTasks ? "assigned-by-me" : "assigned-to-me";
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [workspaceTab, setWorkspaceTab] = useState(defaultWorkspaceTab);
  const [taskTab, setTaskTab] = useState(defaultTaskTab);
  const [biodataTab, setBiodataTab] = useState(defaultTaskTab);

  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [assignedProfiles, setAssignedProfiles] = useState([]);
  const [assignmentResults, setAssignmentResults] = useState([]);
  const [assignmentSearch, setAssignmentSearch] = useState({ q: "", search_by: PROFILE_NO_DISPLAY_SEARCH_KEY });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentBusyProfileId, setAssignmentBusyProfileId] = useState(null);
  const [performanceBusyProfileId, setPerformanceBusyProfileId] = useState(null);
  const [performanceModal, setPerformanceModal] = useState({
    open: false,
    loading: false,
    error: "",
    data: null,
  });

  const [taskUsers, setTaskUsers] = useState([]);
  const [taskUsersLoading, setTaskUsersLoading] = useState(false);
  const [taskDashboard, setTaskDashboard] = useState({
    my_pending: 0,
    my_in_progress: 0,
    my_completed: 0,
    assigned_by_me_open: 0,
    overdue: 0,
    managed_open: 0,
  });
  const [myTasks, setMyTasks] = useState([]);
  const [assignedByMeTasks, setAssignedByMeTasks] = useState([]);
  const [managedTasks, setManagedTasks] = useState([]);
  const [biodataDashboard, setBiodataDashboard] = useState({
    my_pending: 0,
    my_in_progress: 0,
    my_duplicate_review: 0,
    assigned_by_me_open: 0,
  });
  const [myBiodataAssignments, setMyBiodataAssignments] = useState([]);
  const [biodataAssignedByMe, setBiodataAssignedByMe] = useState([]);
  const [biodataLoading, setBiodataLoading] = useState(false);
  const [biodataUploadBusy, setBiodataUploadBusy] = useState(false);
  const [biodataActionBusyId, setBiodataActionBusyId] = useState("");
  const biodataFileInputRef = useRef(null);
  const [biodataForm, setBiodataForm] = useState({
    assigned_to_user_id: "",
    source_label: "",
    notes: "",
    files: [],
  });
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskBusyId, setTaskBusyId] = useState(null);
  const [taskStatusDrafts, setTaskStatusDrafts] = useState({});
  const [taskStatusNotes, setTaskStatusNotes] = useState({});
  const [taskReassignDrafts, setTaskReassignDrafts] = useState({});
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    due_date: "",
    assigned_to_user_id: "",
  });
  const [taskFormMode, setTaskFormMode] = useState("create");
  const [editingTask, setEditingTask] = useState(null);
  const [taskFormModalOpen, setTaskFormModalOpen] = useState(false);
  const [taskStatusModalTask, setTaskStatusModalTask] = useState(null);
  const [taskReassignModalTask, setTaskReassignModalTask] = useState(null);
  const [taskHistoryModal, setTaskHistoryModal] = useState({
    open: false,
    loading: false,
    error: "",
    task: null,
    items: [],
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedAssignee = useMemo(
    () => assignableUsers.find((item) => String(item.id) === String(selectedAssigneeId)) || null,
    [assignableUsers, selectedAssigneeId]
  );
  const defaultTaskAssigneeId = useMemo(
    () => (taskUsers[0]?.id ? String(taskUsers[0].id) : ""),
    [taskUsers]
  );
  const canLoadAssignableUsers = canAssignProfiles || canAssignBiodata;
  const hierarchyBlocked = canLoadAssignableUsers && !loadingUsers && assignableUsers.length === 0;
  const taskHierarchyBlocked = canManageTaskUsers && !taskUsersLoading && taskUsers.length === 0;
  const canUseTaskStatusControls = canUpdateTasks || canCompleteTasks;

  useEffect(() => {
    setWorkspaceTab(defaultWorkspaceTab);
  }, [defaultWorkspaceTab]);

  useEffect(() => {
    setTaskTab(defaultTaskTab);
  }, [defaultTaskTab]);

  useEffect(() => {
    setBiodataTab(defaultTaskTab);
  }, [defaultTaskTab]);

  useEffect(() => {
    if (forcedTaskTab === "assigned-to-me" || forcedTaskTab === "assigned-by-me") {
      setTaskTab(forcedTaskTab);
    }
  }, [forcedTaskTab]);

  useEffect(() => {
    if (forcedWorkspaceTab === "tasks" || forcedWorkspaceTab === "profiles" || forcedWorkspaceTab === "biodata") {
      setWorkspaceTab(forcedWorkspaceTab);
    }
  }, [forcedWorkspaceTab]);

  useEffect(() => {
    if (!canLoadAssignableUsers) {
      setLoadingUsers(false);
      return undefined;
    }

    let cancelled = false;

    const loadAssignableUsers = async () => {
      try {
        setLoadingUsers(true);
        setError("");
        const res = await API.get("/admin/profile-assignments/users", authHeaders);
        const users = Array.isArray(res.data) ? res.data : [];
        if (cancelled) return;
        setAssignableUsers(users);
        setSelectedAssigneeId((current) => {
          if (current && users.some((item) => String(item.id) === String(current))) return current;
          return users[0]?.id ? String(users[0].id) : "";
        });
      } catch (err) {
        if (cancelled) return;
        setAssignableUsers([]);
        setSelectedAssigneeId("");
        setError(err.response?.data?.error || "Failed to load assignable users.");
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };

    loadAssignableUsers();

    return () => {
      cancelled = true;
    };
  }, [authHeaders, canLoadAssignableUsers]);

  useEffect(() => {
    setBiodataForm((current) => ({
      ...current,
      assigned_to_user_id:
        current.assigned_to_user_id && assignableUsers.some((item) => String(item.id) === String(current.assigned_to_user_id))
          ? current.assigned_to_user_id
          : assignableUsers[0]?.id
            ? String(assignableUsers[0].id)
            : "",
    }));
  }, [assignableUsers]);

  useEffect(() => {
    if (!canAssignProfiles || !selectedAssigneeId) {
      setAssignedProfiles([]);
      return undefined;
    }

    let cancelled = false;

    const loadAssignedProfiles = async () => {
      try {
        setAssignmentLoading(true);
        setError("");
        const res = await API.get(`/admin/profile-assignments?user_id=${selectedAssigneeId}`, authHeaders);
        if (cancelled) return;
        setAssignedProfiles(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;
        setAssignedProfiles([]);
        setError(err.response?.data?.error || "Failed to load assigned profiles.");
      } finally {
        if (!cancelled) setAssignmentLoading(false);
      }
    };

    loadAssignedProfiles();

    return () => {
      cancelled = true;
    };
  }, [authHeaders, canAssignProfiles, selectedAssigneeId]);

  useEffect(() => {
    setPerformanceModal({
      open: false,
      loading: false,
      error: "",
      data: null,
    });
    setPerformanceBusyProfileId(null);
  }, [selectedAssigneeId]);

  const hydrateTaskDrafts = useCallback((taskGroups = []) => {
    const uniqueTasks = taskGroups.flat().filter(Boolean);
    setTaskStatusDrafts((current) => {
      const next = { ...current };
      uniqueTasks.forEach((task) => {
        next[String(task.id)] = current[String(task.id)] || task.status || "pending";
      });
      return next;
    });
    setTaskStatusNotes((current) => {
      const next = { ...current };
      uniqueTasks.forEach((task) => {
        if (typeof next[String(task.id)] !== "string") {
          next[String(task.id)] = "";
        }
      });
      return next;
    });
    setTaskReassignDrafts((current) => {
      const next = { ...current };
      uniqueTasks.forEach((task) => {
        next[String(task.id)] = current[String(task.id)] || String(task.assigned_to_user_id || "");
      });
      return next;
    });
  }, []);

  const resetTaskForm = useCallback(() => {
    setTaskForm({
      title: "",
      description: "",
      due_date: "",
      assigned_to_user_id: defaultTaskAssigneeId,
    });
    setTaskFormMode("create");
    setEditingTask(null);
  }, [defaultTaskAssigneeId]);

  const refreshTaskWorkspace = useCallback(async () => {
    if (!canViewTasks) return;

    try {
      setTaskLoading(true);
      setError("");
      const [dashboardRes, myRes, assignedRes, managedRes] = await Promise.all([
        API.get("/tasks/dashboard", authHeaders),
        API.get("/tasks/my", authHeaders),
        API.get("/tasks/assigned-by-me", authHeaders),
        API.get("/tasks/managed", authHeaders),
      ]);
      const nextDashboard = dashboardRes.data || {};
      const nextMyTasks = Array.isArray(myRes.data) ? myRes.data : [];
      const nextAssignedByMe = Array.isArray(assignedRes.data) ? assignedRes.data : [];
      const nextManagedTasks = Array.isArray(managedRes.data) ? managedRes.data : [];

      setTaskDashboard({
        my_pending: Number(nextDashboard.my_pending || 0),
        my_in_progress: Number(nextDashboard.my_in_progress || 0),
        my_completed: Number(nextDashboard.my_completed || 0),
        assigned_by_me_open: Number(nextDashboard.assigned_by_me_open || 0),
        overdue: Number(nextDashboard.overdue || 0),
        managed_open: Number(nextDashboard.managed_open || 0),
      });
      setMyTasks(nextMyTasks);
      setAssignedByMeTasks(nextAssignedByMe);
      setManagedTasks(nextManagedTasks);
      hydrateTaskDrafts([nextMyTasks, nextAssignedByMe, nextManagedTasks]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load tasks.");
    } finally {
      setTaskLoading(false);
    }
  }, [authHeaders, canViewTasks, hydrateTaskDrafts]);

  const refreshBiodataWorkspace = useCallback(async () => {
    if (!canViewBiodataAssignments) return;

    try {
      setBiodataLoading(true);
      setError("");
      const [dashboardRes, myRes, assignedRes] = await Promise.all([
        API.get("/biodata-assignments/dashboard", authHeaders),
        API.get("/biodata-assignments/my", authHeaders),
        API.get("/biodata-assignments/assigned-by-me", authHeaders),
      ]);

      const nextDashboard = dashboardRes.data || {};
      setBiodataDashboard({
        my_pending: Number(nextDashboard.my_pending || 0),
        my_in_progress: Number(nextDashboard.my_in_progress || 0),
        my_duplicate_review: Number(nextDashboard.my_duplicate_review || 0),
        assigned_by_me_open: Number(nextDashboard.assigned_by_me_open || 0),
      });
      setMyBiodataAssignments(Array.isArray(myRes.data) ? myRes.data : []);
      setBiodataAssignedByMe(Array.isArray(assignedRes.data) ? assignedRes.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load biodata assignments.");
    } finally {
      setBiodataLoading(false);
    }
  }, [authHeaders, canViewBiodataAssignments]);

  useEffect(() => {
    if (!canManageTaskUsers) {
      setTaskUsers([]);
      setTaskForm({
        title: "",
        description: "",
        due_date: "",
        assigned_to_user_id: "",
      });
      setTaskFormMode("create");
      setEditingTask(null);
      return undefined;
    }

    let cancelled = false;

    const loadTaskUsers = async () => {
      try {
        setTaskUsersLoading(true);
        setError("");
        const res = await API.get("/tasks/users", authHeaders);
        const users = Array.isArray(res.data) ? res.data : [];
        if (cancelled) return;
        setTaskUsers(users);
        setTaskForm((current) => ({
          ...current,
          assigned_to_user_id:
            current.assigned_to_user_id && users.some((item) => String(item.id) === String(current.assigned_to_user_id))
              ? current.assigned_to_user_id
              : users[0]?.id
                ? String(users[0].id)
                : "",
        }));
      } catch (err) {
        if (cancelled) return;
        setTaskUsers([]);
        setTaskForm((current) => ({ ...current, assigned_to_user_id: "" }));
        setError(err.response?.data?.error || "Failed to load task users.");
      } finally {
        if (!cancelled) setTaskUsersLoading(false);
      }
    };

    loadTaskUsers();

    return () => {
      cancelled = true;
    };
  }, [authHeaders, canManageTaskUsers]);

  useEffect(() => {
    refreshTaskWorkspace();
  }, [refreshTaskWorkspace]);

  useEffect(() => {
    refreshBiodataWorkspace();
  }, [refreshBiodataWorkspace]);

  const runProfileAssignmentSearch = async () => {
    const query = String(assignmentSearch.q || "").trim();
    if (!selectedAssigneeId) {
      setError("Select a target user before searching profiles.");
      return;
    }
    if (!query) {
      setError("Enter a profile number or name to search.");
      return;
    }

    try {
      setAssignmentLoading(true);
      setError("");
      const res = await API.get("/profiles/search", {
        ...authHeaders,
        params: {
          q: query,
          search_by: assignmentSearch.search_by || PROFILE_NO_DISPLAY_SEARCH_KEY,
          limit: 25,
          source: "global_task_assignment",
        },
      });
      setAssignmentResults(Array.isArray(res.data) ? res.data : safeArray(res.data?.items));
    } catch (err) {
      setAssignmentResults([]);
      setError(err.response?.data?.error || "Failed to search profiles for assignment.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const assignProfileToUser = async (profileId) => {
    if (!selectedAssigneeId || !profileId) return;

    try {
      setAssignmentBusyProfileId(String(profileId));
      setError("");
      setSuccess("");
      await API.post(
        "/admin/profile-assignments",
        {
          assigned_user_id: Number(selectedAssigneeId),
          profile_ids: [profileId],
        },
        authHeaders
      );
      setSuccess("Profile assigned.");
      const res = await API.get(`/admin/profile-assignments?user_id=${selectedAssigneeId}`, authHeaders);
      setAssignedProfiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to assign profile.");
    } finally {
      setAssignmentBusyProfileId(null);
    }
  };

  const unassignProfileFromUser = async (profileId) => {
    if (!selectedAssigneeId || !profileId) return;
    if (!window.confirm("Remove this assigned profile?")) return;

    try {
      setAssignmentBusyProfileId(String(profileId));
      setError("");
      setSuccess("");
      await API.delete("/admin/profile-assignments", {
        ...authHeaders,
        data: {
          assigned_user_id: Number(selectedAssigneeId),
          profile_id: profileId,
        },
      });
      setSuccess("Profile unassigned.");
      const res = await API.get(`/admin/profile-assignments?user_id=${selectedAssigneeId}`, authHeaders);
      setAssignedProfiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to unassign profile.");
    } finally {
      setAssignmentBusyProfileId(null);
    }
  };

  const viewAssignedProfileWork = async (profile) => {
    if (!selectedAssigneeId || !profile?.profile_id) return;

    try {
      setPerformanceBusyProfileId(String(profile.profile_id));
      setPerformanceModal({
        open: true,
        loading: true,
        error: "",
        data: null,
      });

      const res = await API.get("/admin/profile-assignments/performance", {
        ...authHeaders,
        params: {
          user_id: selectedAssigneeId,
          profile_id: profile.profile_id,
        },
      });

      setPerformanceModal({
        open: true,
        loading: false,
        error: "",
        data: res.data || null,
      });
    } catch (err) {
      setPerformanceModal({
        open: true,
        loading: false,
        error: err.response?.data?.error || "Failed to load assigned profile performance.",
        data: null,
      });
    } finally {
      setPerformanceBusyProfileId(null);
    }
  };

  const submitTaskForm = async () => {
    const title = String(taskForm.title || "").trim();
    if (!title) {
      setError("Task title is required.");
      return;
    }
    if (taskFormMode === "create" && !taskForm.assigned_to_user_id) {
      setError("Select a task owner.");
      return;
    }

    try {
      setTaskBusyId(taskFormMode === "edit" ? String(editingTask?.id || "") : "create-task");
      setError("");
      setSuccess("");

      if (taskFormMode === "edit" && editingTask?.id) {
        await API.patch(
          `/tasks/${editingTask.id}`,
          {
            title,
            description: String(taskForm.description || "").trim(),
            due_date: taskForm.due_date || null,
          },
          authHeaders
        );
        setSuccess("Task updated.");
      } else {
        await API.post(
          "/tasks",
          {
            title,
            description: String(taskForm.description || "").trim(),
            due_date: taskForm.due_date || null,
            assigned_to_user_id: Number(taskForm.assigned_to_user_id),
          },
          authHeaders
        );
        setSuccess("Task created.");
      }

      resetTaskForm();
      setTaskFormModalOpen(false);
      await refreshTaskWorkspace();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save task.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const startEditingTask = (task) => {
    setWorkspaceTab("tasks");
    setTaskTab("assigned-by-me");
    setTaskFormMode("edit");
    setEditingTask(task);
    setTaskForm({
      title: task?.title || "",
      description: task?.description || "",
      due_date: normalizeDateInput(task?.due_date),
      assigned_to_user_id: String(task?.assigned_to_user_id || ""),
    });
    setTaskFormModalOpen(true);
  };

  const cancelTaskEdit = () => {
    resetTaskForm();
    setTaskFormModalOpen(false);
    setSuccess("");
  };

  const openCreateTaskModal = () => {
    resetTaskForm();
    setTaskFormMode("create");
    setTaskFormModalOpen(true);
    setSuccess("");
  };

  const updateTaskStatus = async (task) => {
    const nextStatus = taskStatusDrafts[String(task.id)] || task.status;
    const statusNote = String(taskStatusNotes[String(task.id)] || "").trim();
    const isStatusChanged = nextStatus && nextStatus !== task.status;
    if (!nextStatus) {
      setError("Select a valid task status.");
      setSuccess("");
      return;
    }
    if (!isStatusChanged && !statusNote) {
      setError("");
      setSuccess("Task status is already up to date. Add a note to post an update.");
      return;
    }

    try {
      setTaskBusyId(`status-${task.id}`);
      setError("");
      setSuccess("");
      await API.patch(
        `/tasks/${task.id}/status`,
        { status: nextStatus, note: statusNote || null },
        authHeaders
      );
      setSuccess(isStatusChanged ? "Task status updated." : "Task update added.");
      setTaskStatusModalTask(null);
      setTaskStatusNotes((current) => ({
        ...current,
        [String(task.id)]: "",
      }));
      await refreshTaskWorkspace();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update task status.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const reassignTask = async (task) => {
    const nextAssignedTo = taskReassignDrafts[String(task.id)] || String(task.assigned_to_user_id || "");
    if (!nextAssignedTo || String(nextAssignedTo) === String(task.assigned_to_user_id || "")) {
      setSuccess("Task owner is already selected.");
      return;
    }

    try {
      setTaskBusyId(`reassign-${task.id}`);
      setError("");
      setSuccess("");
      await API.patch(
        `/tasks/${task.id}/reassign`,
        { assigned_to_user_id: Number(nextAssignedTo) },
        authHeaders
      );
      setSuccess("Task reassigned.");
      setTaskReassignModalTask(null);
      await refreshTaskWorkspace();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reassign task.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const openTaskHistory = async (task) => {
    try {
      setTaskHistoryModal({
        open: true,
        loading: true,
        error: "",
        task,
        items: [],
      });
      const res = await API.get(`/tasks/${task.id}/history`, authHeaders);
      setTaskHistoryModal({
        open: true,
        loading: false,
        error: "",
        task: res.data?.task || task,
        items: Array.isArray(res.data?.items) ? res.data.items : [],
      });
    } catch (err) {
      setTaskHistoryModal({
        open: true,
        loading: false,
        error: err.response?.data?.error || "Failed to load task history.",
        task,
        items: [],
      });
    }
  };

  const closeTaskHistoryModal = () =>
    setTaskHistoryModal({
      open: false,
      loading: false,
      error: "",
      task: null,
      items: [],
    });

  const renderTaskList = (tasks, emptyMessage, options = {}) => (
    <div style={S.card}>
      {taskLoading && tasks.length === 0 ? (
        <div style={S.emptyStateBox}>Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <div style={S.emptyStateBox}>{emptyMessage}</div>
      ) : (
        <div style={S.taskGroupList}>
          {groupTasksByDueBucket(tasks).map((group) => (
            <div key={group.key} style={S.taskGroupSection}>
              <div style={S.taskGroupHeader}>
                <div>
                  <div style={S.taskGroupTitle}>{group.label}</div>
                  <div style={S.taskGroupSubtitle}>{group.description}</div>
                </div>
              </div>

              <div style={S.taskCardGrid} className="jk-task-card-grid">
                {group.items.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    currentUserEmail={currentUserEmail}
                    canOpenStatus={canUseTaskStatusControls}
                    canOpenReassign={options.allowReassign !== false && canReassignTasks && taskUsers.length > 0}
                    canEdit={canUpdateTasks && options.canEdit !== false}
                    isEditing={String(editingTask?.id || "") === String(task.id)}
                    onOpenStatus={() => setTaskStatusModalTask(task)}
                    onOpenReassign={() => setTaskReassignModalTask(task)}
                    onOpenEdit={() => startEditingTask(task)}
                    onOpenHistory={() => openTaskHistory(task)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const openBiodataFile = async (item) => {
    if (!item?.id || !item?.preview_file_id) {
      setError("No biodata file is available to open for this item yet.");
      return;
    }

    try {
      setBiodataActionBusyId(`open-${item.id}`);
      setError("");
      const response = await API.get(`/biodata-assignments/${item.id}/files/${item.preview_file_id}/file`, {
        ...authHeaders,
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to open biodata file.");
    } finally {
      setBiodataActionBusyId("");
    }
  };

  const updateBiodataStatus = async (item, status) => {
    if (!item?.id || !status) return;

    try {
      setBiodataActionBusyId(`status-${item.id}-${status}`);
      setError("");
      setSuccess("");
      await API.patch(
        `/biodata-assignments/${item.id}/status`,
        { status },
        authHeaders
      );
      setSuccess("Biodata assignment updated.");
      await refreshBiodataWorkspace();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update biodata assignment.");
    } finally {
      setBiodataActionBusyId("");
    }
  };

  const openBiodataWorkflow = async (item) => {
    if (!item?.id || typeof onOpenBiodataWorkflow !== "function") return;

    try {
      setBiodataActionBusyId(`workflow-${item.id}`);
      setError("");
      setSuccess("");

      if (item.status === "pending") {
        await API.patch(
          `/biodata-assignments/${item.id}/status`,
          { status: "in_progress" },
          authHeaders
        );
      }

      onOpenBiodataWorkflow(item.id);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to open biodata entry workspace.");
    } finally {
      setBiodataActionBusyId("");
    }
  };

  const submitBiodataUpload = async () => {
    if (!biodataForm.assigned_to_user_id) {
      setError("Select a user before uploading biodata.");
      return;
    }
    if (!safeArray(biodataForm.files).length) {
      setError("Select at least one biodata file to upload.");
      return;
    }

    try {
      setBiodataUploadBusy(true);
      setError("");
      setSuccess("");
      const formData = new FormData();
      formData.append("assigned_to_user_id", String(biodataForm.assigned_to_user_id));
      formData.append("source_label", String(biodataForm.source_label || "").trim());
      formData.append("notes", String(biodataForm.notes || "").trim());
      safeArray(biodataForm.files).forEach((file) => {
        formData.append("files", file, file.name || "biodata-file");
      });

      await API.post("/biodata-assignments/upload", formData, {
        ...authHeaders,
        headers: {
          ...authHeaders.headers,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Biodata uploaded and assigned.");
      setBiodataForm((current) => ({
        ...current,
        source_label: "",
        notes: "",
        files: [],
      }));
      if (biodataFileInputRef.current) {
        biodataFileInputRef.current.value = "";
      }
      await refreshBiodataWorkspace();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to upload biodata.");
    } finally {
      setBiodataUploadBusy(false);
    }
  };

  const renderBiodataList = (title, items, emptyMessage, options = {}) => (
    <div style={S.card}>
      <div style={S.cardHeaderRow}>
        <h2 style={S.cardTitle}>{title}</h2>
        {options.badge ? <span style={S.miniBadge}>{options.badge}</span> : null}
      </div>

      {biodataLoading && items.length === 0 ? (
        <div style={S.emptyStateBox}>Loading biodata assignments...</div>
      ) : items.length === 0 ? (
        <div style={S.emptyStateBox}>{emptyMessage}</div>
      ) : (
        <div style={S.listWrap}>
          {items.map((item) => (
            <div key={item.id} style={S.listRow}>
              <div style={S.listContent}>
                <div style={S.taskTitleRow}>
                  <div style={S.rowTitle}>
                    {item.source_label || item.original_file_name || `Biodata #${item.id}`}
                  </div>
                  <span style={biodataStatusPillStyle(item.status)}>{biodataStatusLabel(item.status)}</span>
                </div>
                <div style={S.muted}>
                  {[
                    item.file_count ? `${item.file_count} file${Number(item.file_count) === 1 ? "" : "s"}` : "No files",
                    item.original_file_name || "",
                  ].filter(Boolean).join(" | ")}
                </div>
                <div style={S.taskMetaLine}>
                  Assigned to: {formatAssignmentActor(item.assigned_to_email || "user", currentUserEmail)}
                  {item.assigned_by_email ? ` | Assigned by: ${formatAssignmentActor(item.assigned_by_email, currentUserEmail)}` : ""}
                </div>
                <div style={S.taskMetaLine}>
                  Uploaded by: {formatAssignmentActor(item.uploaded_by_email || "user", currentUserEmail)}
                  {item.created_at ? ` | Uploaded: ${formatDateTimeLabel(item.created_at)}` : ""}
                </div>
                {item.status === "completed" && item.completed_at ? (
                  <div style={S.taskMetaLine}>
                    Completed: {formatDateTimeLabel(item.completed_at)}
                  </div>
                ) : null}
                {item.status === "duplicate_review" ? (
                  <div style={S.assignmentMeta}>
                    Duplicate review is pending. Open the profile entry workspace to resolve it with the same duplicate options used in normal profile creation.
                  </div>
                ) : null}
                {item.duplicate_check_status === "resolved" ? (
                  <div style={S.assignmentMeta}>
                    Duplicate review was resolved before closure
                    {item.duplicate_resolution ? ` (${String(item.duplicate_resolution).replace(/_/g, " ")})` : ""}.
                  </div>
                ) : null}
                {item.created_profile_id ? (
                  <div style={S.assignmentMeta}>
                    Created profile: #{formatProfileNumberValue(item.created_profile_no, item.created_profile_gender, "-")}
                    {item.created_profile_name ? ` - ${item.created_profile_name}` : ""}
                  </div>
                ) : null}
                {item.notes ? <div style={S.taskDescription}>{item.notes}</div> : null}
              </div>
              <div style={S.rowActions}>
                {item.preview_file_id ? (
                  <button
                    type="button"
                    style={{ ...S.ghostBtn, ...(biodataActionBusyId === `open-${item.id}` ? S.btnDisabled : {}) }}
                    onClick={() => openBiodataFile(item)}
                    disabled={biodataActionBusyId === `open-${item.id}`}
                  >
                    {biodataActionBusyId === `open-${item.id}` ? "Opening..." : "Open File"}
                  </button>
                ) : null}
                {item.created_profile_id && typeof onViewProfile === "function" ? (
                  <button
                    type="button"
                    style={S.ghostBtn}
                    onClick={() => onViewProfile(item.created_profile_id)}
                  >
                    View Profile
                  </button>
                ) : null}
                {options.showStatusActions && item.status !== "completed" ? (
                  <>
                    {canUpdateBiodata && item.status === "pending" && (
                      <button
                        type="button"
                        style={{ ...S.secondaryBtn, ...(biodataActionBusyId === `status-${item.id}-in_progress` ? S.btnDisabled : {}) }}
                        onClick={() => updateBiodataStatus(item, "in_progress")}
                        disabled={biodataActionBusyId === `status-${item.id}-in_progress`}
                      >
                        Start Work
                      </button>
                    )}
                    {item.status !== "pending" && (
                      <button
                        type="button"
                        style={{ ...S.primaryBtn, ...(biodataActionBusyId === `workflow-${item.id}` ? S.btnDisabled : {}) }}
                        onClick={() => openBiodataWorkflow(item)}
                        disabled={biodataActionBusyId === `workflow-${item.id}`}
                      >
                        {biodataActionBusyId === `workflow-${item.id}`
                          ? "Opening..."
                          : item.status === "duplicate_review"
                            ? "Resolve Duplicate"
                            : item.status === "in_progress"
                              ? "Continue Entry"
                              : "Create Profile"}
                      </button>
                    )}
                    {canUpdateBiodata && item.status !== "duplicate_review" && (
                      <button
                        type="button"
                        style={{ ...S.secondaryBtn, ...(biodataActionBusyId === `status-${item.id}-duplicate_review` ? S.btnDisabled : {}) }}
                        onClick={() => updateBiodataStatus(item, "duplicate_review")}
                        disabled={biodataActionBusyId === `status-${item.id}-duplicate_review`}
                      >
                        Mark Duplicate
                      </button>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 1180px) {
          .jk-task-workspace-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-task-card-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 860px) {
          .jk-task-hero-tabs {
            flex-wrap: wrap;
          }
          .jk-task-hero-layout {
            grid-template-columns: 1fr !important;
          }
          .jk-task-card-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-task-toggle-row {
            flex-direction: column;
            align-items: stretch !important;
          }
          .jk-task-modal-actions {
            width: 100%;
          }
          .jk-task-modal-actions button {
            flex: 1 1 0;
          }
        }
      `}</style>

      <div style={S.hero}>
        <div style={S.heroLayout} className="jk-task-hero-layout">
          <div>
            <p style={S.eyebrow}>Operations Workspace</p>
            <div style={S.workspaceTabs} className="jk-task-hero-tabs">
              {hasTaskWorkspaceAccess && (
                <button
                  type="button"
                  style={{
                    ...S.tabBtn,
                    ...(workspaceTab === "tasks" ? S.tabBtnActive : {}),
                  }}
                  onClick={() => setWorkspaceTab("tasks")}
                >
                  Tasks
                </button>
              )}
              {canAssignProfiles && (
                <button
                  type="button"
                  style={{
                    ...S.tabBtn,
                    ...(workspaceTab === "profiles" ? S.tabBtnActive : {}),
                  }}
                  onClick={() => setWorkspaceTab("profiles")}
                >
                  Profile Assignments
                </button>
              )}
              {canViewBiodataAssignments && (
                <button
                  type="button"
                  style={{
                    ...S.tabBtn,
                    ...(workspaceTab === "biodata" ? S.tabBtnActive : {}),
                  }}
                  onClick={() => setWorkspaceTab("biodata")}
                >
                  Bio Data Assignments
                </button>
              )}
            </div>
            <h1 style={S.title}>
              {workspaceTab === "tasks"
                ? "Task Management"
                : workspaceTab === "profiles"
                  ? "Profile Assignments"
                  : "Bio Data Assignments"}
            </h1>
            <p style={S.subtitle}>
              {workspaceTab === "tasks"
                ? "Create internal to-dos, assign them to any active user, and keep status history in one place."
                : workspaceTab === "profiles"
                  ? "Assign profiles to any active user from one global place."
                  : "Manage biodata assignments, operator workloads, and review status."}
            </p>
          </div>

          {workspaceTab === "tasks" && canCreateTasks && !canViewTasks ? (
            <div style={S.heroActionArea}>
              <div style={S.heroSupportText}>
                Your role can create tasks even if the task list is hidden.
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}
      {success && <div style={S.successBox}>{success}</div>}

      {workspaceTab === "profiles" && canAssignProfiles && (
        <>
          {hierarchyBlocked && (
            <div style={S.infoBox}>
              You cannot assign to anyone because no active users are available yet.
            </div>
          )}

          <div style={S.tabRow}>
            <button
              type="button"
              style={S.tabBtn}
              onClick={typeof onSwitchToAssignedToMe === "function" ? onSwitchToAssignedToMe : undefined}
            >
              Assigned To Me
            </button>
            <button type="button" style={{ ...S.tabBtn, ...S.tabBtnActive }}>
              Assigned By Me
            </button>
          </div>

          <div style={S.grid} className="jk-task-workspace-grid">
            <div style={S.card}>
              <h2 style={S.cardTitle}>Target User</h2>

              <Field label="Assignable User">
                <select
                  style={S.input}
                  value={selectedAssigneeId}
                  onChange={(e) => {
                    setSelectedAssigneeId(e.target.value);
                    setAssignmentResults([]);
                    setSuccess("");
                  }}
                  disabled={hierarchyBlocked || loadingUsers}
                >
                  <option value="">{loadingUsers ? "Loading users..." : "Select user"}</option>
                  {assignableUsers.map((assignmentUser) => (
                    <option key={assignmentUser.id} value={assignmentUser.id}>
                      {formatAssignableUserLabel(assignmentUser)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Search By">
                <select
                  style={S.input}
                  value={assignmentSearch.search_by}
                  onChange={(e) => setAssignmentSearch((current) => ({ ...current, search_by: e.target.value }))}
                  disabled={hierarchyBlocked || !selectedAssigneeId}
                >
                  <option value={PROFILE_NO_DISPLAY_SEARCH_KEY}>Profile Number</option>
                  <option value="name">Name</option>
                </select>
              </Field>

              <Field label="Search Profiles">
                <input
                  style={S.input}
                  value={assignmentSearch.q}
                  onChange={(e) => setAssignmentSearch((current) => ({ ...current, q: e.target.value }))}
                  placeholder={isProfileNoDisplaySearchMode(assignmentSearch.search_by) ? "Enter profile number e.g. M123" : "Enter profile name"}
                  disabled={hierarchyBlocked || !selectedAssigneeId}
                />
              </Field>

              <button
                style={{ ...S.primaryBtn, ...(hierarchyBlocked || !selectedAssigneeId ? S.btnDisabled : {}) }}
                onClick={runProfileAssignmentSearch}
                disabled={assignmentLoading || hierarchyBlocked || !selectedAssigneeId}
              >
                {assignmentLoading ? "Searching..." : "Search Profiles"}
              </button>

              <p style={S.helperText}>
                You can assign profiles to any active user.
              </p>
            </div>

            <div style={S.card}>
              <h2 style={S.cardTitle}>Assigned Profiles</h2>

              {!selectedAssigneeId ? (
                <div style={S.emptyStateBox}>
                  {hierarchyBlocked ? "No active users are available for assignment." : "Select a user to view current assignments."}
                </div>
              ) : assignmentLoading && assignedProfiles.length === 0 ? (
                <div style={S.emptyStateBox}>Loading assigned profiles...</div>
              ) : assignedProfiles.length === 0 ? (
                <div style={S.emptyStateBox}>No active profile assignments for this user.</div>
              ) : (
                <div style={S.listWrap}>
                  {assignedProfiles.map((profile) => (
                    <div key={profile.profile_id} style={S.listRow}>
                      <div>
                        <div style={S.rowTitle}>#{getProfileNumberDisplay(profile, "-")} - {profile.full_name || "Unnamed profile"}</div>
                        <div style={S.muted}>{[profile.gender, profile.city, profile.state].filter(Boolean).join(" - ") || "Profile summary unavailable"}</div>
                        {isSuperAdmin && profile.assigned_by_email && (
                          <div style={S.assignmentMeta}>
                            Assigned to {formatAssignmentActor(profile.assigned_user_email || selectedAssignee?.email || "user", currentUserEmail)} by {formatAssignmentActor(profile.assigned_by_email, currentUserEmail)}
                          </div>
                        )}
                      </div>
                      <div style={S.rowActions}>
                        <button
                          style={{ ...S.ghostBtn, ...(performanceBusyProfileId === String(profile.profile_id) ? S.btnDisabled : {}) }}
                          onClick={() => viewAssignedProfileWork(profile)}
                          disabled={performanceBusyProfileId === String(profile.profile_id)}
                        >
                          {performanceBusyProfileId === String(profile.profile_id) ? "Loading..." : "View Work"}
                        </button>
                        {canUnassignProfiles && (
                          <button
                            style={{ ...S.secondaryBtn, ...(assignmentBusyProfileId === String(profile.profile_id) ? S.btnDisabled : {}) }}
                            onClick={() => unassignProfileFromUser(profile.profile_id)}
                            disabled={assignmentBusyProfileId === String(profile.profile_id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>Search Results</h2>

            {!selectedAssigneeId ? (
              <div style={S.emptyStateBox}>
                {hierarchyBlocked ? "No active users are available for assignment." : "Select a target user and search for profiles."}
              </div>
            ) : assignmentResults.length === 0 ? (
              <div style={S.emptyStateBox}>Search for profiles to assign them here.</div>
            ) : (
              <div style={S.listWrap}>
                {assignmentResults.map((profile) => {
                  const activeAssignedUserIds = safeArray(profile.active_assigned_user_ids).map((value) => String(value));
                  const alreadyAssigned =
                    assignedProfiles.some((item) => String(item.profile_id) === String(profile.id)) ||
                    activeAssignedUserIds.includes(String(selectedAssigneeId));
                  return (
                    <div key={profile.id} style={S.listRow}>
                      <div>
                        <div style={S.rowTitle}>#{getProfileNumberDisplay(profile, "-")} - {profile.full_name || "Unnamed profile"}</div>
                        <div style={S.muted}>{[profile.gender, profile.city, profile.state].filter(Boolean).join(" - ") || "No summary available"}</div>
                        {profile.active_assigned_users_summary ? (
                          <div style={S.assignmentMeta}>
                            Assigned to: {profile.active_assigned_users_summary}
                          </div>
                        ) : null}
                      </div>
                      <button
                        style={{ ...S.primaryBtn, ...(alreadyAssigned || assignmentBusyProfileId === String(profile.id) ? S.btnDisabled : {}) }}
                        onClick={() => assignProfileToUser(profile.id)}
                        disabled={alreadyAssigned || assignmentBusyProfileId === String(profile.id)}
                      >
                        {alreadyAssigned ? "Assigned" : "Assign"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {workspaceTab === "biodata" && canViewBiodataAssignments && (
        <>
          <div style={S.tabRow}>
            <button
              type="button"
              style={{
                ...S.tabBtn,
                ...(biodataTab === "assigned-to-me" ? S.tabBtnActive : {}),
              }}
              onClick={() => setBiodataTab("assigned-to-me")}
            >
              Assigned To Me
            </button>
            <button
              type="button"
              style={{
                ...S.tabBtn,
                ...(biodataTab === "assigned-by-me" ? S.tabBtnActive : {}),
              }}
              onClick={() => setBiodataTab("assigned-by-me")}
            >
              Assigned By Me
            </button>
          </div>

          {biodataTab === "assigned-to-me" && (
            <>
              <div style={S.dashboardGrid}>
                <MetricCard label="Pending" value={biodataDashboard.my_pending} />
                <MetricCard label="In Progress" value={biodataDashboard.my_in_progress} />
                <MetricCard label="Duplicate Review" value={biodataDashboard.my_duplicate_review} accent="warn" />
              </div>

              {renderBiodataList(
                "Bio Data Assigned To Me",
                myBiodataAssignments,
                "No biodata assignments are currently assigned to you.",
                { badge: `${myBiodataAssignments.length} items`, showStatusActions: canUpdateBiodata || canCompleteBiodata }
              )}
            </>
          )}

          {biodataTab === "assigned-by-me" && (
            <>
              {canAssignBiodata && (
                <div style={S.card}>
                  <div style={S.cardHeaderRow}>
                    <h2 style={S.cardTitle}>Create Biodata Assignment</h2>
                  </div>

                  <div style={S.formGrid}>
                    <Field label="Assign To">
                      <select
                        style={S.input}
                        value={biodataForm.assigned_to_user_id}
                        onChange={(e) => setBiodataForm((current) => ({ ...current, assigned_to_user_id: e.target.value }))}
                        disabled={loadingUsers || hierarchyBlocked || biodataUploadBusy}
                      >
                        <option value="">{loadingUsers ? "Loading users..." : "Select user"}</option>
                        {assignableUsers.map((assignmentUser) => (
                          <option key={assignmentUser.id} value={assignmentUser.id}>
                            {formatAssignableUserLabel(assignmentUser)}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Reference">
                      <input
                        style={S.input}
                        value={biodataForm.source_label}
                        onChange={(e) => setBiodataForm((current) => ({ ...current, source_label: e.target.value }))}
                        placeholder="Enter reference"
                        disabled={biodataUploadBusy}
                      />
                    </Field>

                    <Field label="Files">
                      <input
                        ref={biodataFileInputRef}
                        style={{ ...S.input, ...S.fileInput }}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.webp"
                        onChange={(e) =>
                          setBiodataForm((current) => ({
                            ...current,
                            files: Array.from(e.target.files || []),
                          }))
                        }
                        disabled={biodataUploadBusy}
                      />
                      <span style={S.helperText}>
                        {safeArray(biodataForm.files).length
                          ? `${safeArray(biodataForm.files).length} file(s) selected`
                          : "Supported: PDF, image, and office document files."}
                      </span>
                    </Field>

                    <Field label="Notes">
                      <textarea
                        style={{ ...S.input, ...S.textarea }}
                        value={biodataForm.notes}
                        onChange={(e) => setBiodataForm((current) => ({ ...current, notes: e.target.value }))}
                        placeholder="Enter notes"
                        disabled={biodataUploadBusy}
                      />
                    </Field>
                  </div>

                  <div style={S.inlineActionWrap}>
                    <button
                      type="button"
                      style={{ ...S.primaryBtn, ...(biodataUploadBusy || hierarchyBlocked ? S.btnDisabled : {}) }}
                      onClick={submitBiodataUpload}
                      disabled={biodataUploadBusy || hierarchyBlocked}
                    >
                      {biodataUploadBusy ? "Creating..." : "Create Assignment"}
                    </button>
                  </div>
                </div>
              )}

              <div style={S.dashboardGrid}>
                <MetricCard label="Open Items" value={biodataDashboard.assigned_by_me_open} />
                <MetricCard label="Needs Review" value={biodataAssignedByMe.filter((item) => item.status === "duplicate_review").length} accent="warn" />
              </div>

              {renderBiodataList(
                "Bio Data Assigned By Me",
                biodataAssignedByMe,
                "You have not assigned any biodata items yet.",
                { badge: `${biodataAssignedByMe.length} items` }
              )}
            </>
          )}
        </>
      )}

      {workspaceTab === "tasks" && hasTaskWorkspaceAccess && (
        <>
          {canManageTaskUsers && taskHierarchyBlocked && (
            <div style={S.infoBox}>
              You have task permissions, but no active users are available for assignment.
            </div>
          )}

          {canViewTasks && (
            <>
              <div style={S.dashboardGrid}>
                <MetricCard label="Assigned To Me" value={myTasks.length} />
                <MetricCard label="Assigned By Me" value={assignedByMeTasks.length} />
                <MetricCard label="Team Open" value={taskDashboard.managed_open} />
                <MetricCard label="Pending" value={taskDashboard.my_pending} />
                <MetricCard label="In Progress" value={taskDashboard.my_in_progress} />
                <MetricCard label="Completed" value={taskDashboard.my_completed} />
                <MetricCard label="Overdue" value={taskDashboard.overdue} accent="warn" />
              </div>

              <div style={S.card}>
                <div style={S.cardHeaderRow}>
                  <div>
                    <h2 style={S.cardTitle}>Existing Tasks</h2>
                    <p style={S.sectionSubtitle}>
                      Switch views to scan your work, tasks you assigned, or visible team activity.
                    </p>
                  </div>
                  <div style={S.headerActionsInline}>
                    <span style={S.miniBadge}>
                      {taskTab === "assigned-to-me"
                        ? `${myTasks.length} items`
                        : taskTab === "assigned-by-me"
                          ? `${assignedByMeTasks.length} items`
                          : `${managedTasks.length} items`}
                    </span>
                    {taskTab === "assigned-by-me" && canCreateTasks ? (
                      <button
                        type="button"
                        style={{ ...S.primaryBtn, ...(taskHierarchyBlocked ? S.btnDisabled : {}) }}
                        onClick={openCreateTaskModal}
                        disabled={taskHierarchyBlocked}
                      >
                        + Add Task
                      </button>
                    ) : null}
                  </div>
                </div>

                <div style={S.toggleRow} className="jk-task-toggle-row">
                  <TaskViewToggle value={taskTab} onChange={setTaskTab} />
                </div>
              </div>

              {taskTab === "assigned-to-me" &&
                renderTaskList(
                  myTasks,
                  "No tasks are currently assigned to you.",
                  { canEdit: false, allowReassign: false }
                )}

              {taskTab === "assigned-by-me" &&
                renderTaskList(
                  assignedByMeTasks,
                  "You have not assigned any tasks yet.",
                  {}
                )}

              {taskTab === "team-tasks" &&
                renderTaskList(
                  managedTasks,
                  "No visible team tasks yet.",
                  {}
                )}
            </>
          )}

          {!canViewTasks && canCreateTasks && (
            <div style={S.card}>
              <div style={S.cardHeaderRow}>
                <div>
                  <h2 style={S.cardTitle}>Task Creation</h2>
                  <p style={S.sectionSubtitle}>
                    Your role can create tasks even when task-list visibility is restricted.
                  </p>
                </div>
                <button
                  type="button"
                  style={{ ...S.primaryBtn, ...(taskHierarchyBlocked ? S.btnDisabled : {}) }}
                  onClick={openCreateTaskModal}
                  disabled={taskHierarchyBlocked}
                >
                  + Add Task
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {workspaceTab === "profiles" && !canAssignProfiles && (
        <div style={S.emptyStateBox}>Your role does not currently have profile assignment access.</div>
      )}
      {workspaceTab === "biodata" && !canViewBiodataAssignments && (
        <div style={S.emptyStateBox}>Your role does not currently have biodata assignment access.</div>
      )}
      {workspaceTab === "tasks" && !hasTaskWorkspaceAccess && (
        <div style={S.emptyStateBox}>Your role does not currently have task management access.</div>
      )}

      <AssignedProfilePerformanceModal
        open={performanceModal.open}
        loading={performanceModal.loading}
        error={performanceModal.error}
        data={performanceModal.data || (selectedAssignee ? { assigned_user: { email: selectedAssignee.email } } : null)}
        onClose={() =>
          setPerformanceModal({
            open: false,
            loading: false,
            error: "",
            data: null,
          })
        }
      />

      {taskFormModalOpen && (
        <TaskModalShell
          title={taskFormMode === "edit" ? "Edit Task" : "Add Task"}
          subtitle={
            taskFormMode === "edit"
              ? "Update task details without changing any existing backend rules."
              : "Create a new internal task with the same existing task logic."
          }
          onClose={cancelTaskEdit}
          footer={(
            <div style={S.modalFooterActions} className="jk-task-modal-actions">
              <button type="button" style={S.ghostBtn} onClick={cancelTaskEdit}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...S.primaryBtn, ...(taskBusyId ? S.btnDisabled : {}) }}
                onClick={submitTaskForm}
                disabled={Boolean(taskBusyId)}
              >
                {taskBusyId === "create-task" || (taskFormMode === "edit" && taskBusyId === String(editingTask?.id || ""))
                  ? "Saving..."
                  : taskFormMode === "edit"
                    ? "Update Task"
                    : "Create Task"}
              </button>
            </div>
          )}
        >
          <div style={S.formGrid}>
            <Field label="Title">
              <input
                style={S.input}
                value={taskForm.title}
                onChange={(e) => setTaskForm((current) => ({ ...current, title: e.target.value }))}
                placeholder="Call prospect family"
              />
            </Field>

            <Field label="Due Date">
              <input
                style={S.input}
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm((current) => ({ ...current, due_date: e.target.value }))}
              />
            </Field>

            {taskFormMode === "create" && (
              <Field label="Assign To">
                <select
                  style={S.input}
                  value={taskForm.assigned_to_user_id}
                  onChange={(e) => setTaskForm((current) => ({ ...current, assigned_to_user_id: e.target.value }))}
                  disabled={taskUsersLoading || taskHierarchyBlocked}
                >
                  <option value="">{taskUsersLoading ? "Loading users..." : "Select user"}</option>
                  {taskUsers.map((taskUser) => (
                    <option key={taskUser.id} value={taskUser.id}>
                      {formatAssignableUserLabel(taskUser)}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Description">
              <textarea
                style={{ ...S.input, ...S.textarea }}
                value={taskForm.description}
                onChange={(e) => setTaskForm((current) => ({ ...current, description: e.target.value }))}
                placeholder="Add the exact follow-up or review work needed"
              />
            </Field>
          </div>
        </TaskModalShell>
      )}

      {taskStatusModalTask && (
        <TaskModalShell
          title="Update Status"
          subtitle={`Current status: ${taskStatusLabel(taskStatusModalTask.status)}`}
          onClose={() => setTaskStatusModalTask(null)}
          footer={(
            <div style={S.modalFooterActions} className="jk-task-modal-actions">
              <button type="button" style={S.ghostBtn} onClick={() => setTaskStatusModalTask(null)}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...S.primaryBtn, ...(taskBusyId === `status-${taskStatusModalTask.id}` ? S.btnDisabled : {}) }}
                onClick={() => updateTaskStatus(taskStatusModalTask)}
                disabled={taskBusyId === `status-${taskStatusModalTask.id}`}
              >
                {taskBusyId === `status-${taskStatusModalTask.id}` ? "Saving..." : "Update Status"}
              </button>
            </div>
          )}
        >
          <div style={S.modalTaskHeading}>{taskStatusModalTask.title || "Untitled task"}</div>
          <div style={S.modalMetaLine}>
            Due: {formatDateLabel(taskStatusModalTask.due_date)} | Owner: {formatAssignmentActor(taskStatusModalTask.assigned_to_email, currentUserEmail)}
          </div>
          <div style={S.formGrid}>
            <Field label="Status">
              <select
                style={S.input}
                value={taskStatusDrafts[String(taskStatusModalTask.id)] || taskStatusModalTask.status || "pending"}
                onChange={(e) =>
                  setTaskStatusDrafts((current) => ({
                    ...current,
                    [String(taskStatusModalTask.id)]: e.target.value,
                  }))
                }
              >
                {TASK_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Update Note">
              <textarea
                style={{ ...S.input, ...S.textarea }}
                value={taskStatusNotes[String(taskStatusModalTask.id)] || ""}
                onChange={(e) =>
                  setTaskStatusNotes((current) => ({
                    ...current,
                    [String(taskStatusModalTask.id)]: e.target.value,
                  }))
                }
                placeholder="Add update note"
              />
            </Field>
          </div>
        </TaskModalShell>
      )}

      {taskReassignModalTask && (
        <TaskModalShell
          title="Reassign Task"
          subtitle="Move ownership using the existing reassignment rules."
          onClose={() => setTaskReassignModalTask(null)}
          footer={(
            <div style={S.modalFooterActions} className="jk-task-modal-actions">
              <button type="button" style={S.ghostBtn} onClick={() => setTaskReassignModalTask(null)}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...S.primaryBtn, ...(taskBusyId === `reassign-${taskReassignModalTask.id}` ? S.btnDisabled : {}) }}
                onClick={() => reassignTask(taskReassignModalTask)}
                disabled={taskBusyId === `reassign-${taskReassignModalTask.id}`}
              >
                {taskBusyId === `reassign-${taskReassignModalTask.id}` ? "Saving..." : "Reassign Task"}
              </button>
            </div>
          )}
        >
          <div style={S.modalTaskHeading}>{taskReassignModalTask.title || "Untitled task"}</div>
          <div style={S.modalMetaLine}>
            Current owner: {formatAssignmentActor(taskReassignModalTask.assigned_to_email || "user", currentUserEmail)}
          </div>
          <Field label="Assign To">
            <select
              style={S.input}
              value={taskReassignDrafts[String(taskReassignModalTask.id)] || String(taskReassignModalTask.assigned_to_user_id || "")}
              onChange={(e) =>
                setTaskReassignDrafts((current) => ({
                  ...current,
                  [String(taskReassignModalTask.id)]: e.target.value,
                }))
              }
            >
              {taskUsers.map((taskUser) => (
                <option key={taskUser.id} value={taskUser.id}>
                  {formatAssignableUserLabel(taskUser)}
                </option>
              ))}
            </select>
          </Field>
        </TaskModalShell>
      )}

      {taskHistoryModal.open && (
        <TaskModalShell
          title={taskHistoryModal.task?.title || "Task History"}
          subtitle="Timeline of assignment and status changes."
          onClose={closeTaskHistoryModal}
          wide
          footer={(
            <div style={S.modalFooterActions} className="jk-task-modal-actions">
              <button type="button" style={S.ghostBtn} onClick={closeTaskHistoryModal}>
                Close
              </button>
            </div>
          )}
        >
          {taskHistoryModal.loading ? (
            <div style={S.emptyStateBox}>Loading task history...</div>
          ) : taskHistoryModal.error ? (
            <div style={S.errorBox}>{taskHistoryModal.error}</div>
          ) : taskHistoryModal.items.length === 0 ? (
            <div style={S.emptyStateBox}>No task activity recorded yet.</div>
          ) : (
            <div style={S.historyTimeline}>
              {taskHistoryModal.items.map((item) => (
                <div key={item.id} style={S.historyTimelineRow}>
                  <div style={S.historyDot} />
                  <div style={S.historyRow}>
                    <div style={S.historyTitle}>{formatTaskActivity(item)}</div>
                    <div style={S.historyMeta}>
                      {formatAssignmentActor(item.changed_by_email || "system", currentUserEmail)} | {formatDateTimeLabel(item.created_at)}
                    </div>
                    {item.note ? <div style={S.taskDescription}>{item.note}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TaskModalShell>
      )}
    </div>
  );
}

function MetricCard({ label, value, accent = "default" }) {
  return (
    <div
      style={{
        ...S.metricCard,
        ...(accent === "warn" ? S.metricCardWarn : {}),
      }}
    >
      <div style={S.metricLabel}>{label}</div>
      <div style={S.metricValue}>{value}</div>
    </div>
  );
}

function TaskViewToggle({ value, onChange }) {
  const options = [
    { value: "assigned-to-me", label: "Assigned To Me" },
    { value: "assigned-by-me", label: "Assigned By Me" },
    { value: "team-tasks", label: "Team Tasks" },
  ];

  return (
    <div style={S.taskViewToggle}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          style={{
            ...S.taskViewBtn,
            ...(value === option.value ? S.taskViewBtnActive : {}),
          }}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function TaskCard({
  task,
  currentUserEmail,
  canOpenStatus,
  canOpenReassign,
  canEdit,
  isEditing,
  onOpenStatus,
  onOpenReassign,
  onOpenEdit,
  onOpenHistory,
}) {
  return (
    <div style={S.taskCard}>
      <div style={S.taskCardTop}>
        <div style={S.taskCardMetaRow}>
          <span style={taskStatusPillStyle(task.status)}>{taskStatusLabel(task.status)}</span>
          <span style={dueBadgeStyle(task.due_date, task.status)}>{dueBadgeLabel(task.due_date, task.status)}</span>
        </div>

        <div style={S.taskCardTitle}>{task.title || "Untitled task"}</div>
        <div style={S.taskPreviewText}>{previewTaskDescription(task.description)}</div>
      </div>

      <div style={S.taskFacts}>
        <div style={S.taskFactItem}>
          <span style={S.taskFactLabel}>Due date</span>
          <span style={S.taskFactValue}>{formatDateLabel(task.due_date)}</span>
        </div>
        <div style={S.taskFactItem}>
          <span style={S.taskFactLabel}>Owner</span>
          <span style={S.taskFactValueTruncate} title={formatAssignmentActor(task.assigned_to_email || "user", currentUserEmail)}>
            {formatAssignmentActor(task.assigned_to_email || "user", currentUserEmail)}
          </span>
        </div>
        <div style={S.taskFactItem}>
          <span style={S.taskFactLabel}>Assigned by</span>
          <span
            style={S.taskFactValueTruncate}
            title={formatAssignmentActor(task.assigned_by_email || task.created_by_email || "system", currentUserEmail)}
          >
            {formatAssignmentActor(task.assigned_by_email || task.created_by_email || "system", currentUserEmail)}
          </span>
        </div>
        {task.completed_at ? (
          <div style={S.taskFactItem}>
            <span style={S.taskFactLabel}>Completed</span>
            <span style={S.taskFactValue}>{formatDateTimeLabel(task.completed_at)}</span>
          </div>
        ) : null}
      </div>

      {task.related_profile_no || task.related_profile_name ? (
        <div style={S.taskLinkedProfile}>
          Linked profile: {task.related_profile_no ? `#${formatProfileNumberValue(task.related_profile_no, task.related_profile_gender)}` : "Profile"}
          {task.related_profile_name ? ` - ${task.related_profile_name}` : ""}
        </div>
      ) : null}

      <div style={S.taskCardActions}>
        {canOpenStatus ? (
          <button type="button" style={S.primaryBtn} onClick={onOpenStatus}>
            Update Status
          </button>
        ) : null}
        {canOpenReassign ? (
          <button type="button" style={S.secondaryBtn} onClick={onOpenReassign}>
            Reassign Task
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            style={{ ...S.ghostBtn, ...(isEditing ? S.btnDisabled : {}) }}
            onClick={onOpenEdit}
            disabled={isEditing}
          >
            {isEditing ? "Editing" : "Edit"}
          </button>
        ) : null}
        <button type="button" style={S.ghostBtn} onClick={onOpenHistory}>
          View History
        </button>
      </div>
    </div>
  );
}

function TaskModalShell({ title, subtitle, onClose, children, footer = null, wide = false }) {
  return (
    <div style={S.modalBackdrop}>
      <div style={{ ...S.modalCard, ...(wide ? S.modalCardWide : {}) }}>
        <div style={S.modalHeader}>
          <div>
            <h3 style={S.modalTitle}>{title}</h3>
            {subtitle ? <p style={S.modalSubtitle}>{subtitle}</p> : null}
          </div>
          <button type="button" style={S.modalCloseBtn} onClick={onClose}>
            Close
          </button>
        </div>

        <div style={S.modalBody}>{children}</div>
        {footer ? <div style={S.modalFooter}>{footer}</div> : null}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      {children}
    </label>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatAssignmentActor(value, currentUserEmail) {
  const raw = String(value || "").trim();
  if (!raw) return "user";
  if (currentUserEmail && raw.toLowerCase() === currentUserEmail) return "me";
  return raw;
}

function formatAssignableUserLabel(user) {
  const displayName = String(user?.display_name || "").trim();
  const roleName = String(user?.effective_role_name || "").trim() || "No role";
  const email = String(user?.email || "").trim();
  return [displayName, roleName, email].filter(Boolean).join(" - ");
}

function taskStatusLabel(status) {
  switch (String(status || "").trim()) {
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    default:
      return "Pending";
  }
}

function taskStatusPillStyle(status) {
  const normalized = String(status || "").trim();
  if (normalized === "completed") {
    return {
      ...S.statusPill,
      color: "#18794e",
      background: "#e8fff1",
      borderColor: "rgba(24,121,78,0.20)",
    };
  }
  if (normalized === "in_progress") {
    return {
      ...S.statusPill,
      color: "#8b4c2e",
      background: "#fff8eb",
      borderColor: "rgba(139,76,46,0.18)",
    };
  }
  return {
    ...S.statusPill,
    color: "#D73C2E",
    background: "#fff2ef",
    borderColor: "rgba(215,60,46,0.18)",
  };
}

function biodataStatusLabel(status) {
  switch (String(status || "").trim()) {
    case "in_progress":
      return "In Progress";
    case "duplicate_review":
      return "Duplicate Review";
    case "completed":
      return "Completed";
    default:
      return "Pending";
  }
}

function biodataStatusPillStyle(status) {
  const normalized = String(status || "").trim();
  if (normalized === "completed") {
    return {
      ...S.statusPill,
      color: "#18794e",
      background: "#e8fff1",
      borderColor: "rgba(24,121,78,0.20)",
    };
  }
  if (normalized === "in_progress") {
    return {
      ...S.statusPill,
      color: "#8b4c2e",
      background: "#fff8eb",
      borderColor: "rgba(139,76,46,0.18)",
    };
  }
  if (normalized === "duplicate_review") {
    return {
      ...S.statusPill,
      color: "#9f1239",
      background: "#fff1f5",
      borderColor: "rgba(159,18,57,0.18)",
    };
  }
  return {
    ...S.statusPill,
    color: "#D73C2E",
    background: "#fff2ef",
    borderColor: "rgba(215,60,46,0.18)",
  };
}

function normalizeDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function formatDateLabel(value) {
  if (!value) return "No due date";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTimeLabel(value) {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTaskActivity(item) {
  const action = String(item?.action_type || "").trim();
  if (action === "created") return "Task created";
  if (action === "updated") return "Task details updated";
  if (action === "reassigned") return "Task reassigned";
  if (action === "completed") return "Task completed";
  if (action === "status_changed") {
    if (String(item?.old_status || "").trim() === String(item?.new_status || "").trim()) {
      return `Update added while task remains ${taskStatusLabel(item?.new_status)}`;
    }
    return `Status changed from ${taskStatusLabel(item?.old_status)} to ${taskStatusLabel(item?.new_status)}`;
  }
  return action ? action.replace(/_/g, " ") : "Task updated";
}

function previewTaskDescription(value) {
  const text = String(value || "").trim();
  return text || "No description added yet.";
}

function dueBadgeStyle(value, status) {
  if (String(status || "").trim() === "completed") {
    return {
      ...S.taskDueBadge,
      color: "#18794e",
      background: "#e8fff1",
      borderColor: "rgba(24,121,78,0.20)",
    };
  }
  const bucket = getTaskDueBucket(value, status);
  if (bucket === "overdue") {
    return {
      ...S.taskDueBadge,
      color: "#b42318",
      background: "#fff1f0",
      borderColor: "rgba(180,35,24,0.16)",
    };
  }
  if (bucket === "today") {
    return {
      ...S.taskDueBadge,
      color: "#8b4c2e",
      background: "#fff8eb",
      borderColor: "rgba(139,76,46,0.18)",
    };
  }
  if (bucket === "upcoming") {
    return {
      ...S.taskDueBadge,
      color: "#2855ae",
      background: "#eef4ff",
      borderColor: "rgba(40,85,174,0.18)",
    };
  }
  return {
    ...S.taskDueBadge,
    color: "#6b6f7d",
    background: "#f7f7f8",
    borderColor: "rgba(37,41,53,0.10)",
  };
}

function dueBadgeLabel(value, status) {
  if (String(status || "").trim() === "completed") return "Completed";
  const bucket = getTaskDueBucket(value, status);
  if (bucket === "overdue") return "Overdue";
  if (bucket === "today") return "Today";
  if (bucket === "upcoming") return "Upcoming";
  return "No Due Date";
}

function getTaskDueBucket(value, status) {
  if (!value) return "no_due_date";

  const dueDate = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(dueDate.getTime())) return "no_due_date";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (dueDate.getTime() < today.getTime()) return "overdue";
  if (dueDate.getTime() === today.getTime()) return "today";
  return "upcoming";
}

function groupTasksByDueBucket(tasks = []) {
  const groups = [
    {
      key: "overdue",
      label: "Overdue",
      description: "Immediate follow-ups that have crossed their due date.",
      items: [],
    },
    {
      key: "today",
      label: "Today",
      description: "Tasks that should be wrapped up today.",
      items: [],
    },
    {
      key: "upcoming",
      label: "Upcoming",
      description: "Scheduled work that is coming up next.",
      items: [],
    },
    {
      key: "no_due_date",
      label: "No Due Date",
      description: "Tasks without a due date still attached.",
      items: [],
    },
  ];

  const bucketMap = new Map(groups.map((group) => [group.key, group]));
  safeArray(tasks).forEach((task) => {
    const bucket = getTaskDueBucket(task?.due_date, task?.status);
    const target = bucketMap.get(bucket) || bucketMap.get("no_due_date");
    target.items.push(task);
  });

  return groups.filter((group) => group.items.length > 0);
}

const S = {
  page: {
    display: "grid",
    gap: 18,
  },
  hero: {
    padding: 28,
    borderRadius: 30,
    background: "#ffffff",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 24px 70px rgba(185,52,58,0.08)",
  },
  heroLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 18,
    alignItems: "start",
  },
  heroActionArea: {
    display: "grid",
    gap: 10,
    justifyItems: "flex-end",
  },
  heroSupportText: {
    color: "#6b6f7d",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.5,
    maxWidth: 260,
    textAlign: "right",
  },
  eyebrow: {
    margin: "0 0 8px",
    color: "#D73C2E",
    fontSize: 11,
    letterSpacing: 2.2,
    textTransform: "uppercase",
    fontWeight: 900,
  },
  workspaceTabs: {
    display: "inline-flex",
    gap: 8,
    marginTop: 10,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: "1px solid rgba(185,52,58,0.14)",
  },
  tabRow: {
    display: "inline-flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: "1px solid rgba(185,52,58,0.14)",
  },
  tabBtn: {
    border: "none",
    background: "transparent",
    color: "#6b6f7d",
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: "#D73C2E",
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.20)",
  },
  title: {
    margin: "12px 0 0",
    color: "#252935",
    fontSize: 34,
    fontWeight: 950,
    letterSpacing: -0.8,
  },
  subtitle: {
    margin: "10px 0 0",
    color: "#6b6f7d",
    fontSize: 15,
    lineHeight: 1.65,
    maxWidth: 760,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
  },
  dashboardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 14,
  },
  metricCard: {
    padding: 18,
    borderRadius: 22,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 16px 42px rgba(37,41,53,0.05)",
  },
  metricCardWarn: {
    background: "#fff8f4",
  },
  metricLabel: {
    color: "#6b6f7d",
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.1,
  },
  metricValue: {
    marginTop: 8,
    color: "#252935",
    fontSize: 28,
    fontWeight: 950,
  },
  card: {
    padding: 22,
    borderRadius: 28,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 18px 52px rgba(37,41,53,0.06)",
    display: "grid",
    gap: 14,
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  headerActionsInline: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  cardTitle: {
    margin: 0,
    color: "#252935",
    fontSize: 18,
    fontWeight: 900,
  },
  sectionSubtitle: {
    margin: "6px 0 0",
    color: "#6b6f7d",
    fontSize: 14,
    lineHeight: 1.55,
  },
  miniBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#fff3ef",
    color: "#B9343A",
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid rgba(185,52,58,0.14)",
  },
  formGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  },
  field: {
    display: "grid",
    gap: 6,
  },
  label: {
    color: "#252935",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  input: {
    width: "100%",
    minHeight: 50,
    borderRadius: 16,
    border: "1px solid rgba(185,52,58,0.18)",
    background: "#fff",
    color: "#252935",
    padding: "0 16px",
    fontSize: 15,
    boxSizing: "border-box",
  },
  fileInput: {
    paddingTop: 12,
    paddingBottom: 12,
    paddingRight: 12,
  },
  compactInput: {
    minWidth: 150,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(185,52,58,0.18)",
    background: "#fff",
    color: "#252935",
    padding: "0 12px",
    fontSize: 14,
  },
  textarea: {
    minHeight: 120,
    paddingTop: 14,
    paddingBottom: 14,
    resize: "vertical",
  },
  compactTextarea: {
    width: "100%",
    minHeight: 74,
    borderRadius: 14,
    border: "1px solid rgba(185,52,58,0.18)",
    background: "#fff",
    color: "#252935",
    padding: "12px 14px",
    fontSize: 14,
    boxSizing: "border-box",
    resize: "vertical",
  },
  primaryBtn: {
    height: 48,
    padding: "0 20px",
    border: "none",
    borderRadius: 999,
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 900,
    cursor: "pointer",
    justifySelf: "flex-start",
    boxShadow: "0 16px 36px rgba(185,52,58,0.22)",
  },
  secondaryBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(185,52,58,0.18)",
    background: "#fff",
    color: "#252935",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  ghostBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(37,41,53,0.10)",
    background: "rgba(37,41,53,0.04)",
    color: "#252935",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  btnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  helperText: {
    margin: 0,
    color: "#6b6f7d",
    fontSize: 14,
    lineHeight: 1.65,
  },
  errorBox: {
    padding: 14,
    borderRadius: 18,
    background: "#fff2ef",
    color: "#D73C2E",
    fontWeight: 800,
    border: "1px solid rgba(185,52,58,0.16)",
  },
  successBox: {
    padding: 14,
    borderRadius: 18,
    background: "#f6fff8",
    color: "#18794e",
    fontWeight: 800,
    border: "1px solid rgba(24,121,78,0.16)",
  },
  infoBox: {
    padding: 16,
    borderRadius: 18,
    background: "#fff8f4",
    color: "#8b4c2e",
    fontWeight: 800,
    border: "1px solid rgba(185,52,58,0.16)",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  taskViewToggle: {
    display: "inline-flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: "1px solid rgba(185,52,58,0.14)",
    flexWrap: "wrap",
  },
  taskViewBtn: {
    border: "none",
    background: "transparent",
    color: "#6b6f7d",
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  taskViewBtnActive: {
    background: "#D73C2E",
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.20)",
  },
  listWrap: {
    display: "grid",
    gap: 12,
  },
  listRow: {
    padding: 16,
    borderRadius: 22,
    border: "1px solid rgba(185,52,58,0.12)",
    background: "#fff",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  listContent: {
    display: "grid",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  taskRow: {
    padding: 16,
    borderRadius: 22,
    border: "1px solid rgba(185,52,58,0.12)",
    background: "#fff",
    display: "grid",
    gap: 14,
  },
  taskMain: {
    display: "grid",
    gap: 8,
  },
  taskTitleRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  rowTitle: {
    color: "#252935",
    fontSize: 17,
    fontWeight: 900,
  },
  muted: {
    color: "#6b6f7d",
    fontSize: 14,
    lineHeight: 1.5,
  },
  assignmentMeta: {
    color: "#8b4c2e",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  rowActions: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  taskActions: {
    display: "grid",
    gap: 10,
    justifyItems: "flex-start",
  },
  inlineControlRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  statusControlBlock: {
    display: "grid",
    gap: 10,
    width: "100%",
    maxWidth: 360,
  },
  inlineActionWrap: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  taskDescription: {
    color: "#4b5160",
    fontSize: 14,
    lineHeight: 1.65,
  },
  taskMetaLine: {
    color: "#6b6f7d",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  taskGroupList: {
    display: "grid",
    gap: 18,
  },
  taskGroupSection: {
    display: "grid",
    gap: 12,
  },
  taskGroupHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  taskGroupTitle: {
    color: "#252935",
    fontSize: 16,
    fontWeight: 900,
  },
  taskGroupSubtitle: {
    marginTop: 4,
    color: "#6b6f7d",
    fontSize: 13,
    lineHeight: 1.5,
  },
  taskCardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },
  taskCard: {
    minWidth: 0,
    padding: 16,
    borderRadius: 24,
    border: "1px solid rgba(185,52,58,0.12)",
    background: "#fff",
    boxShadow: "0 16px 42px rgba(37,41,53,0.05)",
    display: "grid",
    gap: 14,
    alignContent: "start",
  },
  taskCardTop: {
    display: "grid",
    gap: 10,
    minWidth: 0,
  },
  taskCardMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  taskDueBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  taskCardTitle: {
    color: "#252935",
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1.35,
  },
  taskPreviewText: {
    color: "#4b5160",
    fontSize: 14,
    lineHeight: 1.6,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: 67,
  },
  taskFacts: {
    display: "grid",
    gap: 10,
    minWidth: 0,
  },
  taskFactItem: {
    display: "grid",
    gap: 4,
    minWidth: 0,
  },
  taskFactLabel: {
    color: "#8a8f9d",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  taskFactValue: {
    color: "#252935",
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.45,
  },
  taskFactValueTruncate: {
    color: "#252935",
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.45,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  taskLinkedProfile: {
    color: "#8b4c2e",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
    padding: "10px 12px",
    borderRadius: 16,
    background: "#fff8f4",
    border: "1px solid rgba(185,52,58,0.12)",
  },
  taskCardActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: "auto",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 12,
    fontWeight: 900,
  },
  emptyStateBox: {
    padding: 18,
    borderRadius: 18,
    background: "#fff8f6",
    color: "#6b6f7d",
    fontWeight: 700,
    border: "1px dashed rgba(185,52,58,0.18)",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(25, 27, 35, 0.58)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 2000,
  },
  modalCard: {
    width: "min(760px, 100%)",
    maxHeight: "calc(100vh - 40px)",
    overflow: "hidden",
    padding: 24,
    borderRadius: 28,
    background: "#ffffff",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 28px 90px rgba(18,22,32,0.28)",
    display: "grid",
    gap: 18,
    gridTemplateRows: "auto minmax(0, 1fr) auto",
  },
  modalCardWide: {
    width: "min(920px, 100%)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  modalCloseBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(37,41,53,0.10)",
    background: "rgba(37,41,53,0.04)",
    color: "#252935",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  modalBody: {
    overflow: "auto",
    display: "grid",
    gap: 16,
    paddingRight: 4,
  },
  modalFooter: {
    position: "sticky",
    bottom: 0,
    background: "#ffffff",
    paddingTop: 12,
    borderTop: "1px solid rgba(37,41,53,0.08)",
  },
  modalFooterActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  modalTaskHeading: {
    color: "#252935",
    fontSize: 18,
    fontWeight: 900,
  },
  modalMetaLine: {
    color: "#6b6f7d",
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },
  modalTitle: {
    margin: 0,
    color: "#252935",
    fontSize: 22,
    fontWeight: 950,
  },
  modalSubtitle: {
    margin: "6px 0 0",
    color: "#6b6f7d",
    fontSize: 14,
    lineHeight: 1.5,
  },
  historyList: {
    display: "grid",
    gap: 12,
  },
  historyTimeline: {
    display: "grid",
    gap: 14,
  },
  historyTimelineRow: {
    display: "grid",
    gridTemplateColumns: "16px minmax(0, 1fr)",
    gap: 12,
    alignItems: "stretch",
  },
  historyDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    background: "#D73C2E",
    marginTop: 16,
    boxShadow: "0 0 0 4px rgba(215,60,46,0.12)",
  },
  historyRow: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(185,52,58,0.12)",
    background: "#fff8f6",
    display: "grid",
    gap: 6,
  },
  historyTitle: {
    color: "#252935",
    fontSize: 15,
    fontWeight: 900,
  },
  historyMeta: {
    color: "#6b6f7d",
    fontSize: 13,
    fontWeight: 700,
  },
};
